import { useEffect } from 'react';
import { GlobalStyles } from '@mui/material';

import muiOverrides from 'constants/muiOverrides';

/**
 * MuiOverridesProvider
 *
 * Applies MUI component overrides without using ThemeProvider.
 *
 * This component:
 * 1. Converts styleOverrides to CSS-in-JS format and applies them via GlobalStyles
 * 2. Stores defaultProps globally for access by components that need them
 *
 * Usage:
 * - Add <MuiOverridesProvider /> to your app root (after CssBaseline)
 * - For components that need defaultProps (especially React elements like icons),
 *   use the getMuiDefaultProps utility or access window.__MUI_DEFAULT_PROPS__
 *
 * Note: Some defaultProps containing React components (like icons) cannot be
 * applied automatically and may need to be handled in component wrappers.
 */
const MuiOverridesProvider = () => {
    // Convert styleOverrides to CSS-in-JS format for GlobalStyles
    const globalStyles = buildGlobalStyles();

    // Apply defaultProps via useEffect
    useEffect(() => {
        applyDefaultProps();
    }, []);

    return <GlobalStyles styles={globalStyles} />;
};

/**
 * Builds global styles from muiOverrides
 */
const buildGlobalStyles = (): Record<string, any> => {
    const styles: Record<string, any> = {};

    // Process each component
    Object.keys(muiOverrides.components).forEach((componentName) => {
        //@ts-ignore
        const component = muiOverrides.components[componentName];
        const baseComponentName = componentName.replace('Mui', '');

        // Handle components with direct style properties (like MuiInputLabel)
        if (component.root || component.shrink) {
            if (component.root) {
                styles[`.Mui${baseComponentName}-root`] = convertStyles(component.root, componentName);
            }
            if (component.shrink) {
                styles[`.Mui${baseComponentName}-shrink`] = convertStyles(component.shrink, componentName);
            }
        }

        // Handle components with styleOverrides
        if (component.styleOverrides) {
            Object.keys(component.styleOverrides).forEach((slot) => {
                const slotStyles = component.styleOverrides[slot];
                const selector = `.Mui${baseComponentName}-${slot}`;

                // Handle function styles (like MuiCard.root)
                if (typeof slotStyles === 'function') {
                    const defaultTheme = {
                        palette: {
                            divider: 'rgba(0, 0, 0, 0.12)',
                        },
                    };
                    const computedStyles = slotStyles({ theme: defaultTheme });
                    styles[selector] = convertStyles(computedStyles, componentName);
                } else {
                    styles[selector] = convertStyles(slotStyles, componentName);
                }
            });
        }
    });

    return styles;
};

/**
 * Converts nested style objects to CSS format, handling MUI-specific selectors
 * @param styles - The styles object to convert
 * @param componentName - The MUI component name (e.g., 'MuiSwitch') for resolving $ references
 */
const convertStyles = (styles: any, componentName?: string): any => {
    if (!styles || typeof styles !== 'object' || Array.isArray(styles)) {
        return styles;
    }

    const result: any = {};
    const baseComponentName = componentName?.replace('Mui', '') || '';

    Object.keys(styles).forEach((key) => {
        const value = styles[key];

        // Handle MUI state classes (like $checked, $disabled)
        if (key.startsWith('$') && !key.includes('&')) {
            // Convert $checked to .Mui-checked, etc.
            const stateClass = key.replace('$', '.Mui-');
            if (typeof value === 'object' && value !== null) {
                result[stateClass] = convertStyles(value, componentName);
            } else {
                result[stateClass] = value;
            }
        }
        // Handle pseudo-selectors and nested selectors (like &:hover, &$checked:hover, & + $track)
        else if (key.startsWith('&')) {
            // Convert $checked and $disabled in selectors to .Mui-checked and .Mui-disabled
            let convertedKey = key;
            convertedKey = convertedKey.replace(/\$checked/g, '.Mui-checked');
            convertedKey = convertedKey.replace(/\$disabled/g, '.Mui-disabled');

            // Convert component-specific $ references (like $track, $thumb) to full class names
            if (componentName) {
                // Common MUI component slot names that might be referenced with $
                const slotNames = ['track', 'thumb', 'switchBase', 'notchedOutline', 'input', 'select'];
                slotNames.forEach((slot) => {
                    const regex = new RegExp(`\\$${slot}`, 'g');
                    if (regex.test(convertedKey)) {
                        convertedKey = convertedKey.replace(regex, `.Mui${baseComponentName}-${slot}`);
                    }
                });
            }

            if (typeof value === 'object' && value !== null) {
                result[convertedKey] = convertStyles(value, componentName);
            } else {
                result[convertedKey] = value;
            }
        }
        // Handle media queries
        else if (key.startsWith('@media')) {
            result[key] = convertStyles(value, componentName);
        }
        // Regular CSS property
        else {
            result[key] = value;
        }
    });

    return result;
};

/**
 * Applies defaultProps to MUI components
 * Stores them globally for access by components that need them
 */
const applyDefaultProps = () => {
    const defaultPropsMap: Record<string, any> = {};

    Object.keys(muiOverrides.components).forEach((componentName) => {
        //@ts-ignore
        const component = muiOverrides.components[componentName];

        if (component.defaultProps) {
            defaultPropsMap[componentName] = component.defaultProps;
        }
    });

    // Store in window for access by wrapper components if needed
    if (typeof window !== 'undefined') {
        (window as any).__MUI_DEFAULT_PROPS__ = defaultPropsMap;
    }
};

export default MuiOverridesProvider;
